import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const THRESHOLD = 70;
const MAX_PULL = 120;

export const PullToRefresh = ({ onRefresh }) => {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const pulling = useRef(false);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);

  useEffect(() => {
    const onTouchStart = (e) => {
      if (refreshingRef.current) return;
      // iOS rubber-band can report negative scrollY — treat <= 0 as top
      if (window.scrollY <= 0) {
        startY.current = e.touches[0].clientY;
        pulling.current = false;
      } else {
        startY.current = null;
      }
    };

    const onTouchMove = (e) => {
      if (startY.current === null || refreshingRef.current) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        if (!pulling.current) startY.current = null;
        return;
      }
      if (!pulling.current && dy > 8) {
        // ignore pulls that start inside an independently scrolled element
        let el = e.target;
        while (el && el !== document.body) {
          if (el.scrollTop > 0) { startY.current = null; return; }
          el = el.parentElement;
        }
        pulling.current = true;
      }
      if (pulling.current) {
        const dist = Math.min(MAX_PULL, dy * 0.45);
        pullRef.current = dist;
        setPull(dist);
        if (e.cancelable) e.preventDefault();
      }
    };

    const onTouchEnd = async () => {
      if (!pulling.current) { startY.current = null; return; }
      pulling.current = false;
      startY.current = null;
      if (pullRef.current >= THRESHOLD) {
        pullRef.current = 0;
        refreshingRef.current = true;
        setRefreshing(true);
        setPull(THRESHOLD);
        try { if (onRefresh) await onRefresh(); } catch { /* reload anyway */ }
        window.location.reload();
      } else {
        pullRef.current = 0;
        setPull(0);
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [onRefresh]);

  if (pull <= 0 && !refreshing) return null;
  const ready = pull >= THRESHOLD;

  return (
    <div
      className="fixed inset-x-0 top-0 z-[200] flex justify-center pointer-events-none"
      style={{
        transform: `translateY(${pull - 48}px)`,
        transition: pull === 0 ? 'transform 0.25s ease-out' : 'none',
      }}
      data-testid="pull-to-refresh"
    >
      <div className={`mt-1 w-11 h-11 rounded-full shadow-lg border flex items-center justify-center ${ready || refreshing ? 'bg-orange-500 border-orange-400' : 'bg-white border-gray-200'}`}>
        <RefreshCw
          className={`w-5 h-5 ${ready || refreshing ? 'text-white' : 'text-gray-500'} ${refreshing ? 'animate-spin' : ''}`}
          style={!refreshing ? { transform: `rotate(${pull * 2.5}deg)`, transition: 'transform 0.05s linear' } : undefined}
        />
      </div>
    </div>
  );
};

export default PullToRefresh;
